import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import _ from 'lodash';
import getStateManager from '../StateManager';
import { settingControls, useSavedSettings } from '../Tabs/settingFunctions';
import {
  applyFieldDraft,
  createFieldSettingsDraft,
  pruneFieldDraft,
  updateFieldDraft
} from '../Utils/settings_draft';
import {
  applyNutritionDraft,
  createNutritionDraftStore,
  getNutritionEditorSession,
  isNutritionField,
  loadNutritionDraft,
  nutritionSavePatch,
  nutritionScope,
  nutritionValues,
  sendNutritionSave
} from '../Utils/nutrition_utils';

export function useNutritionSettings() {
  const { t } = useTranslation();
  const params = useParams();
  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);
  const scope = nutritionScope(controller, parent);
  const snapshot = useSnapshot(parent);
  const nutrition = snapshot.settings.nutrition;
  // Submitted values remain visible for retries, separately from unsaved edits
  // and slider previews, including after the editor remounts during reconnect.
  const { submittedFields, previousBaseline } = getNutritionEditorSession(controller, parent);
  if (!previousBaseline.current) previousBaseline.current = nutritionValues(nutrition);
  const [values, setValues] = useState(() =>
    applyNutritionDraft(nutrition, submittedFields.current)
  );
  const displayedFields = useRef({});
  const preview = useRef({});
  const displayFields = useCallback(
    () => updateFieldDraft(submittedFields.current, Object.entries(displayedFields.current)),
    [submittedFields]
  );
  const draft = useMemo(() => {
    const fields = createFieldSettingsDraft(
      createNutritionDraftStore(controller, parent),
      () => applyNutritionDraft(parent.settings.nutrition, submittedFields.current),
      true
    );
    return {
      ...fields,
      remove: (paths) => {
        const baseline = nutritionValues(parent.settings.nutrition);
        submittedFields.current = updateFieldDraft(
          submittedFields.current,
          paths.map((path) => [path, _.get(baseline, path)]),
          baseline,
          true
        );
        fields.remove(paths);
      },
      // Keep invalid stale edits available for reset, but do not recreate
      // recipes removed by the controller while displaying a restored draft.
      restore: () => applyNutritionDraft(
        parent.settings.nutrition,
        updateFieldDraft(submittedFields.current, Object.entries(fields.read()))
      )
    };
  }, [controller, parent, submittedFields]);

  // Hydrate before useSavedSettings runs the existing restore/discard flow.
  useEffect(() => {
    loadNutritionDraft(controller, parent);
  }, [controller, parent]);
  useEffect(() => {
    const baseline = nutritionValues(nutrition);
    const previous = applyFieldDraft(previousBaseline.current, submittedFields.current);
    // As in Multi IO, retire a submission only when its field changes to the
    // requested value. Unrelated readback must not acknowledge a pending revert.
    submittedFields.current = pruneFieldDraft(
      submittedFields.current,
      baseline,
      previousBaseline.current
    );
    const editableBaseline = applyNutritionDraft(nutrition, submittedFields.current);
    displayedFields.current = pruneFieldDraft(displayedFields.current, editableBaseline, previous);
    draft.reconcile(editableBaseline, previous);
    previousBaseline.current = baseline;
    setValues(
      applyNutritionDraft(
        nutrition,
        updateFieldDraft(displayFields(), Object.entries(preview.current))
      )
    );
  }, [displayFields, draft, nutrition, previousBaseline, submittedFields]);

  let invalid = false;
  try {
    nutritionSavePatch(nutrition, snapshot.settings.irrigation_tank_info, scope, displayFields());
  } catch (_) {
    invalid = true;
  }
  const controls = settingControls(controller, parent, 'nutrition', values, setValues, {
    draft,
    getBaseline: () => nutritionValues(parent.settings.nutrition),
    canEdit: () => Boolean(controller.access_rights?.nutrition?.write),
    isEditablePath: isNutritionField,
    // Recipe wire fields are integers; remove scaled-slider conversion noise.
    normalizeValue: (path, value) =>
      path.startsWith('nutrient_settings') && typeof value === 'number' && Number.isFinite(value)
        ? Math.round(value)
        : value,
    onChange: ({ type, entries = [], saveTemp }) => {
      if (type === 'restore') {
        displayedFields.current = _.cloneDeep(draft.read());
        preview.current = {};
      } else if (type === 'resetAll') {
        submittedFields.current = {};
        displayedFields.current = {};
        preview.current = {};
      } else if (type === 'edit' && !saveTemp) {
        entries.forEach(([path, value]) => {
          preview.current[path] = value;
        });
      } else {
        entries.forEach(([path]) => {
          delete preview.current[path];
        });
        displayedFields.current = updateFieldDraft(
          displayedFields.current,
          entries,
          type === 'reset'
            ? nutritionValues(parent.settings.nutrition)
            : applyNutritionDraft(parent.settings.nutrition, submittedFields.current),
          true
        );
      }
    },
    submit: () => {
      if (invalid || Object.keys(preview.current).length) return false;
      const fields = displayFields();
      if (!sendNutritionSave(stateManager, controller, parent, fields)) return false;
      submittedFields.current = fields;
      displayedFields.current = {};
      return true;
    }
  });
  // The restore/discard choice concerns unsaved edits only. The page Reset
  // above separately cancels both those edits and pending submissions.
  const loadSettingsControls = {
    ...controls,
    resetSettings: () => {
      draft.clear();
      displayedFields.current = {};
      preview.current = {};
      setValues(applyNutritionDraft(parent.settings.nutrition, submittedFields.current));
    }
  };
  const savedSettings = useSavedSettings(
    parent,
    loadSettingsControls,
    'nutrition',
    () => !_.isEmpty(draft.read())
  );
  return {
    t,
    controller,
    parent,
    values,
    controls,
    loadSettingsControls,
    ...savedSettings,
    tankSettings: true,
    saveDisabled: invalid || Object.keys(preview.current).length > 0,
    invalid
  };
}
